import { createApi } from './lib/api.js';
import {
	escapeHtml,
	formatMoney,
	parseQueryServiceId,
	parseQueryStaffId,
	getServicePrice,
	formatDateLabel,
	formatShortDateLabel,
	formatTimeLabel,
	formatLiveDateTime,
	todayInTimezone,
	couponDiscountLabel,
	downloadIcs,
	toIcsUtc,
	getInitials,
} from './lib/utils.js';
import { createCalendar } from './lib/calendar.js';
import { mountCardElement, unmountCard, confirmPayment } from './lib/stripe.js';
import { renderStepIndicator } from './components/step-indicator.js';
import { applyThemeVars } from './lib/apply-theme.js';

const STEP_HEADERS = {
	1: 'What service do you need?',
	2: 'Choose your preferred staff',
	3: 'Pick a date and time',
	4: 'Your details',
	5: 'Have a discount code?',
	6: 'Complete your booking',
	7: "You're booked! 🎉",
};

const CHECK_ICON =
	'<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M4 9l3.5 3.5L14 5.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const CALENDAR_ICON =
	'<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>';

const REFERRAL_OPTIONS = [
	{ value: '', label: 'Select an option' },
	{ value: 'Google', label: 'Google' },
	{ value: 'Social Media', label: 'Social Media' },
	{ value: 'Friend / Family', label: 'Friend / Family' },
	{ value: 'Walk-in', label: 'Walk-in' },
	{ value: 'Advertisement', label: 'Advertisement' },
	{ value: 'Other', label: 'Other' },
];

const REGION_OPTIONS = [
	{ value: '', label: 'Select your state or province' },
	{ value: 'AL', label: 'Alabama' },
	{ value: 'AK', label: 'Alaska' },
	{ value: 'AZ', label: 'Arizona' },
	{ value: 'AR', label: 'Arkansas' },
	{ value: 'CA', label: 'California' },
	{ value: 'CO', label: 'Colorado' },
	{ value: 'CT', label: 'Connecticut' },
	{ value: 'DE', label: 'Delaware' },
	{ value: 'FL', label: 'Florida' },
	{ value: 'GA', label: 'Georgia' },
	{ value: 'HI', label: 'Hawaii' },
	{ value: 'ID', label: 'Idaho' },
	{ value: 'IL', label: 'Illinois' },
	{ value: 'IN', label: 'Indiana' },
	{ value: 'IA', label: 'Iowa' },
	{ value: 'KS', label: 'Kansas' },
	{ value: 'KY', label: 'Kentucky' },
	{ value: 'LA', label: 'Louisiana' },
	{ value: 'ME', label: 'Maine' },
	{ value: 'MD', label: 'Maryland' },
	{ value: 'MA', label: 'Massachusetts' },
	{ value: 'MI', label: 'Michigan' },
	{ value: 'MN', label: 'Minnesota' },
	{ value: 'MS', label: 'Mississippi' },
	{ value: 'MO', label: 'Missouri' },
	{ value: 'MT', label: 'Montana' },
	{ value: 'NE', label: 'Nebraska' },
	{ value: 'NV', label: 'Nevada' },
	{ value: 'NH', label: 'New Hampshire' },
	{ value: 'NJ', label: 'New Jersey' },
	{ value: 'NM', label: 'New Mexico' },
	{ value: 'NY', label: 'New York' },
	{ value: 'NC', label: 'North Carolina' },
	{ value: 'ND', label: 'North Dakota' },
	{ value: 'OH', label: 'Ohio' },
	{ value: 'OK', label: 'Oklahoma' },
	{ value: 'OR', label: 'Oregon' },
	{ value: 'PA', label: 'Pennsylvania' },
	{ value: 'RI', label: 'Rhode Island' },
	{ value: 'SC', label: 'South Carolina' },
	{ value: 'SD', label: 'South Dakota' },
	{ value: 'TN', label: 'Tennessee' },
	{ value: 'TX', label: 'Texas' },
	{ value: 'UT', label: 'Utah' },
	{ value: 'VT', label: 'Vermont' },
	{ value: 'VA', label: 'Virginia' },
	{ value: 'WA', label: 'Washington' },
	{ value: 'WV', label: 'West Virginia' },
	{ value: 'WI', label: 'Wisconsin' },
	{ value: 'WY', label: 'Wyoming' },
	{ value: 'DC', label: 'District of Columbia' },
	{ value: 'AB', label: 'Alberta' },
	{ value: 'BC', label: 'British Columbia' },
	{ value: 'MB', label: 'Manitoba' },
	{ value: 'NB', label: 'New Brunswick' },
	{ value: 'NL', label: 'Newfoundland and Labrador' },
	{ value: 'NS', label: 'Nova Scotia' },
	{ value: 'NT', label: 'Northwest Territories' },
	{ value: 'NU', label: 'Nunavut' },
	{ value: 'ON', label: 'Ontario' },
	{ value: 'PE', label: 'Prince Edward Island' },
	{ value: 'QC', label: 'Quebec' },
	{ value: 'SK', label: 'Saskatchewan' },
	{ value: 'YT', label: 'Yukon' },
];

function reportField(el, message) {
	if (!el) {
		return false;
	}
	el.setCustomValidity(message);
	el.reportValidity();
	el.setCustomValidity('');
	return false;
}

/**
 * Customer step validation (avoids HTML5 reportValidity / other plugins on name="phone").
 */
function validateCustomerForm(form) {
	const first = form.querySelector('[name="first_name"]');
	const last = form.querySelector('[name="last_name"]');
	const emailEl = form.querySelector('[name="email"]');
	const phoneEl = form.querySelector('[name="customer_phone"]');
	const dobEl = form.querySelector('[name="customer_dob"]');
	const streetEl = form.querySelector('[name="street_address"]');
	const cityEl = form.querySelector('[name="city"]');
	const stateEl = form.querySelector('[name="state_province"]');
	const postalEl = form.querySelector('[name="postal_code"]');

	if (!first?.value.trim()) {
		return reportField(first, 'Please enter your first name.');
	}
	if (!last?.value.trim()) {
		return reportField(last, 'Please enter your last name.');
	}
	if (!emailEl?.value.trim()) {
		return reportField(emailEl, 'Please enter your email.');
	}
	if (!emailEl.validity.valid) {
		return reportField(emailEl, 'Please enter a valid email address.');
	}
	if (!phoneEl?.value.trim()) {
		return reportField(phoneEl, 'Please enter your phone number.');
	}
	if (!dobEl?.value) {
		return reportField(dobEl, 'Please enter your date of birth.');
	}
	if (!streetEl?.value.trim()) {
		return reportField(streetEl, 'Please enter your street address.');
	}
	if (!cityEl?.value.trim()) {
		return reportField(cityEl, 'Please enter your city.');
	}
	if (!stateEl?.value) {
		return reportField(stateEl, 'Please select your state or province.');
	}
	if (!postalEl?.value.trim()) {
		return reportField(postalEl, 'Please enter your postal code.');
	}

	return true;
}

function emptyCustomer() {
	return {
		first_name: '',
		last_name: '',
		email: '',
		phone: '',
		dob: '',
		parent_guardian_name: '',
		parent_guardian_phone: '',
		street_address: '',
		city: '',
		state_province: '',
		postal_code: '',
		referral_source: '',
	};
}

function optionsHtml(options, selected) {
	return options
		.map((opt) => {
			const sel = String(selected || '') === String(opt.value) ? ' selected' : '';
			return (
				'<option value="' +
				escapeHtml(opt.value) +
				'"' +
				sel +
				'>' +
				escapeHtml(opt.label) +
				'</option>'
			);
		})
		.join('');
}

export function initWizard(root, cfg) {
	const api = createApi(cfg);
	let config = {
		payment_modes: cfg.paymentModes || ['online', 'offline'],
		deposit_pct: 50,
		currency: cfg.currency || 'USD',
		business_name: '',
		offline_instructions:
			'Please pay when you arrive for your appointment.',
		min_advance_booking_hours: 1,
		max_advance_booking_days: 90,
		stripe_publishable_key: cfg.stripePublicKey || '',
		same_day_bookings: true,
		timezone: cfg.timezone || '',
		timezone_label: cfg.timezoneLabel || '',
		time_format: cfg.timeFormat || '12hr',
		date_format: cfg.dateFormat || 'DD/MM/YYYY',
	};

	const state = {
		step: 1,
		loading: false,
		error: '',
		services: [],
		staffList: [],
		slots: [],
		slotsLoading: false,
		workingHours: [],
		unavailableDates: {},
		monthAvailabilityKey: '',
		service: null,
		staff: null,
		skippedStaffStep: false,
		assigningStaff: false,
		summaryCollapsed: true,
		date: '',
		time: '',
		customer: emptyCustomer(),
		coupon: null,
		couponCode: '',
		couponError: '',
		paymentMode: '',
		booking: null,
	};

	let calendarWidget = null;
	let cardMounted = false;
	let assignTimer = null;
	let tzClockTimer = null;

	function currency() {
		return config.currency || cfg.currency || 'USD';
	}

	function displayTimeOptions() {
		return {
			timezone: config.timezone || cfg.timezone || '',
			hour12: (config.time_format || cfg.timeFormat || '12hr') !== '24hr',
			dateFormat: config.date_format || cfg.dateFormat || 'DD/MM/YYYY',
		};
	}

	function slotTimeLabel(slot) {
		if (slot && slot.time_label) {
			return slot.time_label;
		}
		return formatTimeLabel(slot.time, state.date, displayTimeOptions());
	}

	function subtotal() {
		return getServicePrice(state.service, state.staff);
	}

	function discountAmount() {
		if (!state.coupon || !state.coupon.valid) {
			return 0;
		}
		return Number(state.coupon.discount_amount) || 0;
	}

	function totalAfterCoupon() {
		const base = subtotal();
		if (state.coupon && state.coupon.valid) {
			return Number(state.coupon.final_amount) ?? base - discountAmount();
		}
		return base;
	}

	function depositPercent() {
		const servicePct =
			state.service && state.service.deposit_pct != null
				? Number(state.service.deposit_pct)
				: null;
		if (servicePct != null && servicePct > 0) {
			return Math.min(100, Math.max(1, servicePct));
		}
		const sitePct = Number(config.deposit_pct);
		return Math.min(100, Math.max(1, sitePct > 0 ? sitePct : 50));
	}

	function chargeAmount(mode) {
		const total = totalAfterCoupon();
		if (mode === 'deposit') {
			return Math.round(total * (depositPercent() / 100) * 100) / 100;
		}
		return total;
	}

	function setError(msg) {
		state.error = msg || '';
	}

	function setLoading(on) {
		state.loading = !!on;
	}

	function stopTzClock() {
		if (tzClockTimer) {
			clearInterval(tzClockTimer);
			tzClockTimer = null;
		}
	}

	function startTzClock() {
		stopTzClock();
		const tz = config.timezone || cfg.timezone;
		if (!tz) {
			return;
		}
		const tick = () => {
			const el = root.querySelector('[data-tz-clock-value]');
			if (el) {
				el.textContent = formatLiveDateTime(displayTimeOptions());
			}
		};
		tick();
		tzClockTimer = setInterval(tick, 1000);
	}

	function datetimeClockHtml() {
		const tz = config.timezone || cfg.timezone;
		const label = config.timezone_label || tz;
		if (!tz) {
			return '';
		}
		return (
			'<div class="br-datetime__live" data-tz-clock>' +
			'<p class="br-datetime__live-label">Now in ' +
			escapeHtml(label) +
			'</p>' +
			'<p class="br-datetime__live-value" data-tz-clock-value>' +
			escapeHtml(formatLiveDateTime(displayTimeOptions())) +
			'</p></div>'
		);
	}

	function datetimeSelectionHtml() {
		if (!state.date) {
			return '';
		}
		const datePart = formatShortDateLabel(state.date, displayTimeOptions());
		const timePart = state.time
			? formatTimeLabel(state.time, state.date, displayTimeOptions())
			: '';
		return (
			'<p class="br-datetime__selection">' +
			'<span class="br-datetime__selection-label">Your selection:</span> ' +
			escapeHtml(datePart) +
			(timePart ? ' · ' + escapeHtml(timePart) : '') +
			'</p>'
		);
	}

	function goToStep(step) {
		if (assignTimer) {
			clearTimeout(assignTimer);
			assignTimer = null;
		}
		if (step !== 3) {
			stopTzClock();
		}
		state.step = Math.max(1, Math.min(7, step));
		state.error = '';
		state.assigningStaff = false;
		if (state.step !== 6) {
			unmountCard();
			cardMounted = false;
		}
		render();
	}

	/** Only allow navigating to an earlier completed step (never forward). */
	function goToStepFromTab(target) {
		const step = parseInt(target, 10);
		if (!step || step >= state.step || step < 1) {
			return;
		}
		if (state.step === 7) {
			return;
		}
		if (state.step === 3 && state.skippedStaffStep && step === 2) {
			goToStep(1);
			return;
		}
		goToStep(step);
	}

	function goBack() {
		if (state.step <= 1) {
			return;
		}
		if (state.step === 3 && state.skippedStaffStep) {
			goToStep(1);
			return;
		}
		goToStep(state.step - 1);
	}

	function todayDateStr() {
		const tz = config.timezone || cfg.timezone;
		if (tz) {
			return todayInTimezone(tz);
		}
		return todayInTimezone();
	}

	function isDateDisabled(dateStr) {
		const [y, m, d] = dateStr.split('-').map(Number);
		const day = new Date(y, m - 1, d);
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const todayStr = todayDateStr();

		if (dateStr < todayStr) {
			return true;
		}

		if (config.same_day_bookings === false && dateStr === todayStr) {
			return true;
		}

		if (state.staff) {
			const from = state.staff.available_from;
			const until = state.staff.available_until;
			if (from && dateStr < from) {
				return true;
			}
			if (until && dateStr > until) {
				return true;
			}
		}

		const minHours = Number(config.min_advance_booking_hours) || 0;
		if (minHours > 0) {
			const cutoff = new Date(Date.now() + minHours * 3600000);
			const tz = config.timezone || cfg.timezone;
			const cutoffStr = tz
				? new Intl.DateTimeFormat('en-CA', {
						timeZone: tz,
						year: 'numeric',
						month: '2-digit',
						day: '2-digit',
					}).format(cutoff)
				: cutoff.getFullYear() +
					'-' +
					String(cutoff.getMonth() + 1).padStart(2, '0') +
					'-' +
					String(cutoff.getDate()).padStart(2, '0');
			if (dateStr < cutoffStr) {
				return true;
			}
		}

		const maxDays = Number(config.max_advance_booking_days) || 90;
		const maxDate = new Date(today);
		maxDate.setDate(maxDate.getDate() + maxDays);
		if (day > maxDate) {
			return true;
		}

		if (Object.prototype.hasOwnProperty.call(state.unavailableDates, dateStr)) {
			return !!state.unavailableDates[dateStr];
		}

		return false;
	}

	async function loadMonthAvailability(year, month) {
		if (!state.service || !state.staff || !year || !month) {
			return;
		}
		const key = state.service.id + '-' + state.staff.id + '-' + year + '-' + month;
		if (state.monthAvailabilityKey === key) {
			return;
		}
		state.monthAvailabilityKey = key;
		try {
			const data = await api.get('/availability/month', {
				service_id: state.service.id,
				staff_id: state.staff.id,
				year,
				month,
			});
			const next = { ...state.unavailableDates };
			(data.unavailable_dates || []).forEach((dateStr) => {
				next[dateStr] = true;
			});
			(data.available_dates || []).forEach((dateStr) => {
				next[dateStr] = false;
			});
			state.unavailableDates = next;
			if (state.date && state.unavailableDates[state.date]) {
				state.date = '';
				state.time = '';
				state.slots = [];
				state.workingHours = [];
			}
			if (calendarWidget) {
				calendarWidget.refresh();
			} else if (state.step === 3) {
				render();
			}
		} catch (err) {
			// Keep calendar usable with rule-based disabled dates only.
			state.monthAvailabilityKey = '';
		}
	}

	function autoAssignStaff(member) {
		state.staff = member;
		state.skippedStaffStep = true;
		state.assigningStaff = true;
		state.unavailableDates = {};
		state.monthAvailabilityKey = '';
		state.workingHours = [];
		goToStep(2);
		assignTimer = setTimeout(() => {
			state.assigningStaff = false;
			goToStep(3);
		}, 500);
	}

	async function loadConfig() {
		try {
			const data = await api.get('/public/config');
			config = { ...config, ...data };
			if (data.stripe_publishable_key) {
				cfg.stripePublicKey = data.stripe_publishable_key;
			}
			if (data.currency) {
				cfg.currency = data.currency;
			}
			if (data.timezone) {
				cfg.timezone = data.timezone;
			}
			if (data.timezone_label) {
				cfg.timezoneLabel = data.timezone_label;
			}
			if (data.time_format) {
				cfg.timeFormat = data.time_format;
			}
			if (data.date_format) {
				cfg.dateFormat = data.date_format;
			}
		} catch (e) {
			/* use localized defaults */
		}
	}

	async function loadServices() {
		setLoading(true);
		setError('');
		try {
			state.services = (await api.get('/services')) || [];
			const pre =
				parseInt(root.getAttribute('data-service-id'), 10) ||
				cfg.preselectedService ||
				parseQueryServiceId();
			if (pre) {
				const found = state.services.find((s) => s.id === pre);
				if (found) {
					selectService(found, true);
					return;
				}
			}
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
			render();
		}
	}

	function selectService(service, autoAdvance) {
		state.service = service;
		state.staff = null;
		state.staffList = [];
		state.date = '';
		state.time = '';
		state.coupon = null;
		state.couponCode = '';
		loadStaff(!!autoAdvance);
	}

	async function loadStaff(autoAdvance) {
		if (!state.service) {
			return;
		}
		setLoading(true);
		setError('');
		try {
			const staffQuery = state.date ? { date: state.date } : {};
			state.staffList =
				(await api.get('/services/' + state.service.id + '/staff', staffQuery)) || [];

			const preStaffId = parseQueryStaffId();
			if (preStaffId) {
				const preStaff = state.staffList.find((s) => s.id === preStaffId);
				if (preStaff) {
					autoAssignStaff(preStaff);
					return;
				}
			}

			if (state.staffList.length === 1) {
				autoAssignStaff(state.staffList[0]);
			} else if (!state.staffList.length) {
				setError('No staff available for this service.');
				state.skippedStaffStep = false;
				goToStep(2);
			} else {
				state.skippedStaffStep = false;
				if (autoAdvance) {
					goToStep(2);
				} else {
					goToStep(2);
				}
			}
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
			if (!state.assigningStaff) {
				render();
			}
		}
	}

	function selectStaff(member) {
		state.staff = member;
		state.unavailableDates = {};
		state.monthAvailabilityKey = '';
		state.workingHours = [];
		if (!state.date || isDateDisabled(state.date)) {
			state.date = '';
			state.time = '';
		}
		goToStep(3);
	}

	async function loadSlots() {
		if (!state.service || !state.staff || !state.date) {
			return;
		}
		state.slotsLoading = true;
		state.slots = [];
		state.workingHours = [];
		renderSlotsOnly();
		try {
			const data = await api.get('/availability', {
				service_id: state.service.id,
				staff_id: state.staff.id,
				date: state.date,
			});
			state.slots = data.slots || [];
			state.workingHours = data.working_hours || [];
			const hasOpen = (state.slots || []).some((slot) => slot.available);
			state.unavailableDates = {
				...state.unavailableDates,
				[state.date]: !hasOpen,
			};
		} catch (err) {
			setError(err.message);
		} finally {
			state.slotsLoading = false;
			render();
		}
	}

	function renderSlotsOnly() {
		const el = root.querySelector('[data-slots]');
		if (el) {
			el.innerHTML = renderSlotsHtml();
			bindSlotClicks();
		}
	}

	function renderSlotsHtml() {
		if (state.slotsLoading) {
			return '<p class="br-muted">Loading times…</p>';
		}
		if (!state.date) {
			return '<p class="br-muted">Select a date to see available times.</p>';
		}
		const bookable = state.slots.filter((slot) => slot.available);
		if (!bookable.length) {
			return '<p class="br-muted">No slots available for this date.</p>';
		}
		return (
			'<div class="br-slot-grid">' +
			bookable
				.map((slot) => {
					const avail = slot.available;
					const sel = state.time === slot.time;
					return (
						'<button type="button" class="br-slot' +
						(avail ? '' : ' is-unavailable') +
						(sel ? ' is-selected' : '') +
						'" data-time="' +
						escapeHtml(slot.time) +
						'"' +
						(avail ? '' : ' disabled') +
						'>' +
						escapeHtml(slotTimeLabel(slot)) +
						'</button>'
					);
				})
				.join('') +
			'</div>'
		);
	}

	async function applyCoupon() {
		const code = state.couponCode.trim();
		if (!code) {
			state.couponError = 'Enter a coupon code.';
			render();
			return;
		}
		setLoading(true);
		state.couponError = '';
		try {
			const result = await api.post('/coupons/validate', {
				code,
				service_id: state.service.id,
				amount: subtotal(),
			});
			if (!result.valid) {
				state.coupon = null;
				state.couponError = 'Invalid or expired coupon';
			} else {
				state.coupon = result;
				state.couponCode = code;
				state.couponError = '';
			}
		} catch (err) {
			state.couponError = err.message || 'Invalid or expired coupon';
		} finally {
			setLoading(false);
			render();
		}
	}

	function removeCoupon() {
		state.coupon = null;
		state.couponCode = '';
		state.couponError = '';
		render();
	}

	function defaultPaymentMode() {
		const modes = config.payment_modes || [];
		return modes[0] || 'offline';
	}

	async function submitBooking() {
		setLoading(true);
		setError('');
		const mode = state.paymentMode || defaultPaymentMode();
		const c = state.customer;
		const payload = {
			service_id: state.service.id,
			staff_id: state.staff.id,
			booking_date: state.date,
			booking_time: state.time,
			customer_first_name: c.first_name.trim(),
			customer_last_name: c.last_name.trim(),
			customer_name: (c.first_name + ' ' + c.last_name).trim(),
			customer_email: c.email.trim(),
			customer_phone: c.phone.trim(),
			customer_dob: c.dob,
			parent_guardian_name: c.parent_guardian_name.trim(),
			parent_guardian_phone: c.parent_guardian_phone.trim(),
			street_address: c.street_address.trim(),
			city: c.city.trim(),
			state_province: c.state_province,
			postal_code: c.postal_code.trim(),
			referral_source: c.referral_source,
			payment_mode: mode,
			website: root.querySelector('[name="website"]')
				? root.querySelector('[name="website"]').value
				: '',
		};
		if (state.coupon && state.coupon.valid) {
			payload.coupon_code = state.couponCode;
		}

		try {
			const booking = await api.post('/bookings', payload);
			state.booking = booking;

			if (
				booking.client_secret &&
				(mode === 'online' || mode === 'deposit')
			) {
				const key =
					config.stripe_publishable_key || cfg.stripePublicKey;
				await confirmPayment(key, booking.client_secret);
				state.booking = await api.post(
					'/bookings/' + booking.id + '/payment-complete',
					{ cancel_token: booking.cancel_token }
				);
			}

			goToStep(7);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
			render();
		}
	}

	function renderServiceList() {
		if (!state.services.length) {
			return '<p class="br-muted">No services are available right now.</p>';
		}
		return (
			'<ul class="br-service-list">' +
			state.services
				.map((s) => {
					const selected = state.service && state.service.id === s.id;
					const img = s.service_image
						? '<img src="' +
							escapeHtml(s.service_image) +
							'" alt="" loading="lazy" />'
						: '<span class="br-service-row__icon" aria-hidden="true">' +
							escapeHtml(getInitials(s.name)) +
							'</span>';
					const category = s.category
						? '<span class="br-pill">' + escapeHtml(s.category) + '</span>'
						: '';
					return (
						'<li><button type="button" class="br-service-row' +
						(selected ? ' is-selected' : '') +
						'" data-service-id="' +
						s.id +
						'">' +
						'<span class="br-service-row__media">' +
						img +
						'</span>' +
						'<span class="br-service-row__body">' +
						'<h4 class="br-service-row__title">' +
						escapeHtml(s.name) +
						'</h4>' +
						'<span class="br-service-row__meta">' +
						category +
						'<span>' +
						escapeHtml(String(s.duration)) +
						' min</span>' +
						'<span>From ' +
						escapeHtml(formatMoney(s.base_price, currency())) +
						'</span></span></span>' +
						'<span class="br-service-row__action">Select →</span></button></li>'
					);
				})
				.join('') +
			'</ul>'
		);
	}

	function staffPriceHtml(member) {
		const base = Number(state.service.base_price) || 0;
		const price = getServicePrice(state.service, member);
		if (member.price_override != null && price !== base) {
			return (
				'<p class="br-staff-card__price">' +
				escapeHtml(formatMoney(price, currency())) +
				'</p>'
			);
		}
		return '';
	}

	function renderStaffCards() {
		if (state.assigningStaff && state.staff) {
			return (
				'<div class="br-staff-assigning" aria-live="polite">' +
				'<span class="br-spinner"></span>' +
				'<p>Assigning ' +
				escapeHtml(state.staff.full_name) +
				'…</p></div>'
			);
		}
		if (!state.staffList.length) {
			return '<p class="br-muted">No staff members for this service.</p>';
		}
		return (
			'<div class="br-staff-grid">' +
			state.staffList
				.map((s) => {
					const selected = state.staff && state.staff.id === s.id;
					const photo = s.photo
						? '<img class="br-staff-card__photo" src="' +
							escapeHtml(s.photo) +
							'" alt="" loading="lazy" />'
						: '<span class="br-staff-card__photo br-staff-card__photo--placeholder" aria-hidden="true">' +
							escapeHtml(getInitials(s.full_name)) +
							'</span>';
					return (
						'<button type="button" class="br-staff-card' +
						(selected ? ' is-selected' : '') +
						'" data-staff-id="' +
						s.id +
						'">' +
						photo +
						'<h3 class="br-staff-card__name">' +
						escapeHtml(s.full_name) +
						'</h3>' +
						(s.role_title
							? '<p class="br-staff-card__role">' +
								escapeHtml(s.role_title) +
								'</p>'
							: '') +
						staffPriceHtml(s) +
						'<span class="br-staff-card__btn">Select</span></button>'
					);
				})
				.join('') +
			'</div>'
		);
	}

	function renderCustomerForm() {
		const c = state.customer;
		return (
			'<form class="br-form" data-form="customer" novalidate>' +
			'<input type="text" name="website" class="br-honeypot" tabindex="-1" autocomplete="off" />' +
			'<div class="br-form-row">' +
			'<label class="br-field"><span>First Name *</span><input class="br-input" type="text" name="first_name" autocomplete="given-name" required placeholder="First Name" value="' +
			escapeHtml(c.first_name) +
			'" /></label>' +
			'<label class="br-field"><span>Last Name *</span><input class="br-input" type="text" name="last_name" autocomplete="family-name" required placeholder="Last Name" value="' +
			escapeHtml(c.last_name) +
			'" /></label></div>' +
			'<div class="br-form-row">' +
			'<label class="br-field"><span>Email *</span><input class="br-input" type="email" name="email" autocomplete="email" required placeholder="Email" value="' +
			escapeHtml(c.email) +
			'" /></label>' +
			'<label class="br-field"><span>Phone *</span><input class="br-input" type="text" name="customer_phone" inputmode="tel" autocomplete="tel" required placeholder="Phone" value="' +
			escapeHtml(c.phone) +
			'" /></label></div>' +
			'<label class="br-field"><span>Date of Birth *</span><input class="br-input" type="date" name="customer_dob" required value="' +
			escapeHtml(c.dob) +
			'" /></label>' +
			'<div class="br-form-row">' +
			'<label class="br-field"><span>Parent/Guardian Full Name <span class="br-muted">(optional)</span></span><input class="br-input" type="text" name="parent_guardian_name" placeholder="Enter parent or guardian\'s full name" value="' +
			escapeHtml(c.parent_guardian_name) +
			'" /></label>' +
			'<label class="br-field"><span>Parent/Guardian Phone Number <span class="br-muted">(optional)</span></span><input class="br-input" type="text" name="parent_guardian_phone" inputmode="tel" placeholder="Enter parent or guardian\'s phone number" value="' +
			escapeHtml(c.parent_guardian_phone) +
			'" /></label></div>' +
			'<label class="br-field"><span>Street Address *</span><input class="br-input" type="text" name="street_address" autocomplete="street-address" required placeholder="Enter your street address" value="' +
			escapeHtml(c.street_address) +
			'" /></label>' +
			'<div class="br-form-row br-form-row--3">' +
			'<label class="br-field"><span>City *</span><input class="br-input" type="text" name="city" autocomplete="address-level2" required placeholder="Enter your city" value="' +
			escapeHtml(c.city) +
			'" /></label>' +
			'<label class="br-field"><span>State / Province *</span><select class="br-input br-select" name="state_province" required>' +
			optionsHtml(REGION_OPTIONS, c.state_province) +
			'</select></label>' +
			'<label class="br-field"><span>Postal Code *</span><input class="br-input" type="text" name="postal_code" autocomplete="postal-code" required placeholder="Enter your postal code" value="' +
			escapeHtml(c.postal_code) +
			'" /></label></div>' +
			'<label class="br-field"><span>How Did You Hear About Us? <span class="br-muted">(optional)</span></span><select class="br-input br-select" name="referral_source">' +
			optionsHtml(REFERRAL_OPTIONS, c.referral_source) +
			'</select></label>' +
			'<button type="submit" class="br-btn-primary br-btn--block">Continue</button>' +
			'</form>'
		);
	}

	function renderStepBody() {
		switch (state.step) {
			case 1:
				return renderServiceList();
			case 2:
				return renderStaffCards();
			case 3:
				return (
					'<div class="br-datetime">' +
					datetimeClockHtml() +
					'<div class="br-datetime__cal" data-calendar></div>' +
					'<div class="br-datetime__slots br-br-scrollbar">' +
					(state.staff
						? '<p class="br-datetime__staff-hours">' +
							'<span class="br-datetime__staff-name">' +
							escapeHtml(state.staff.full_name) +
							'</span>' +
							(state.workingHours.length
								? '<span class="br-datetime__hours-label"> · ' +
									escapeHtml(
										state.workingHours.map((w) => w.label).join(', ')
									) +
									'</span>'
								: state.date
									? '<span class="br-datetime__hours-label"> · No working hours this day</span>'
									: '') +
							'</p>'
						: '') +
					datetimeSelectionHtml() +
					(state.date
						? '<h3 class="br-datetime__slots-title">Available times for ' +
							escapeHtml(
								formatShortDateLabel(state.date, displayTimeOptions())
							) +
							'</h3>'
						: '') +
					'<div data-slots>' +
					renderSlotsHtml() +
					'</div>' +
					(state.date && state.time
						? '<button type="button" class="br-btn-primary br-btn--block br-datetime__continue" data-action="datetime-continue">Continue</button>'
						: '') +
					'</div></div>'
				);
			case 4:
				return renderCustomerForm();
			case 5:
				return renderCouponStep();
			case 6:
				return renderPaymentStep();
			case 7:
				return renderConfirmation();
			default:
				return '';
		}
	}

	function renderCouponStep() {
		const applied = state.coupon && state.coupon.valid;
		let formBlock = '';
		if (applied) {
			formBlock =
				'<div class="br-coupon-success">' +
				CHECK_ICON +
				'<span>Coupon applied</span></div>' +
				'<p class="br-coupon-discount-line">' +
				escapeHtml(couponDiscountLabel(state.coupon, currency())) +
				'</p>' +
				'<button type="button" class="br-text-link" data-action="remove-coupon">Remove</button>';
		} else {
			formBlock =
				'<form class="br-coupon-row" data-form="coupon">' +
				'<input class="br-input" type="text" name="code" placeholder="Enter coupon code" value="' +
				escapeHtml(state.couponCode) +
				'" />' +
				'<button type="submit" class="br-btn-primary">Apply</button>' +
				'</form>' +
				(state.couponError
					? '<p class="br-error-text">' +
						escapeHtml(state.couponError) +
						'</p>'
					: '') +
				'<div class="br-coupon-skip-wrap">' +
				'<button type="button" class="br-btn-skip-coupon" data-action="skip-coupon">' +
				'<span>Continue without coupon</span>' +
				'<span class="br-btn-skip-coupon__arrow" aria-hidden="true">→</span>' +
				'</button></div>';
		}
		if (applied) {
			formBlock +=
				'<button type="button" class="br-btn-primary br-btn--block" style="margin-top:20px" data-action="coupon-continue">Continue to payment</button>';
		}
		return '<div class="br-coupon">' + formBlock + '</div>';
	}

	function paymentModeMeta(mode) {
		const cur = currency();
		const total = formatMoney(totalAfterCoupon(), cur);
		const charge = formatMoney(chargeAmount(mode), cur);
		const map = {
			online: {
				icon: '💳',
				label: 'Pay in Full — ' + charge,
				desc: 'Credit or debit card via Stripe',
			},
			deposit: {
				icon: '💳',
				label: 'Pay Deposit — ' + charge,
				desc: 'Secure card deposit, remainder due at appointment',
			},
			offline: {
				icon: '📅',
				label: 'Pay Later',
				desc: 'Confirm your booking, pay at the appointment',
			},
		};
		return map[mode] || { icon: '•', label: mode, desc: '' };
	}

	function renderPaymentStep() {
		const modes = config.payment_modes || ['offline'];
		const mode = state.paymentMode || defaultPaymentMode();
		const needsStripe =
			(mode === 'online' || mode === 'deposit') &&
			(config.stripe_publishable_key || cfg.stripePublicKey);

		return (
			'<div class="br-payment">' +
			'<fieldset class="br-payment-modes">' +
			modes
				.map((m) => {
					const meta = paymentModeMeta(m);
					const sel = mode === m;
					return (
						'<label class="br-payment-card' +
						(sel ? ' is-selected' : '') +
						'">' +
						'<input type="radio" name="payment_mode" value="' +
						escapeHtml(m) +
						'"' +
						(sel ? ' checked' : '') +
						' />' +
						'<span class="br-payment-card__radio" aria-hidden="true"></span>' +
						'<span class="br-payment-card__icon" aria-hidden="true">' +
						meta.icon +
						'</span>' +
						'<span class="br-payment-card__body">' +
						'<span class="br-payment-card__label">' +
						escapeHtml(meta.label) +
						'</span>' +
						'<span class="br-payment-card__desc">' +
						escapeHtml(meta.desc) +
						'</span></span></label>'
					);
				})
				.join('') +
			'</fieldset>' +
			(needsStripe
				? '<div class="br-stripe-wrap"><div id="br-stripe-card"></div></div>'
				: '') +
			'<button type="button" class="br-btn-primary br-btn-book" data-action="pay"' +
			(state.loading ? ' disabled' : '') +
			'>' +
			(state.loading ? 'Processing…' : 'Book Appointment') +
			'</button></div>'
		);
	}

	function renderConfirmation() {
		const b = state.booking || {};
		const paid = Number(b.amount_paid) || 0;
		const offline = (b.payment_mode || state.paymentMode) === 'offline';
		const showPaid = !offline && b.status === 'confirmed';

		if (offline) {
			return (
				'<div class="br-confirmation">' +
				'<div class="br-confirmation-info"><p>Your booking is reserved. Payment is due at your appointment.</p></div>' +
				renderConfirmationDetails(b, paid, showPaid, offline) +
				'</div>'
			);
		}

		return (
			'<div class="br-confirmation">' +
			'<div class="br-confirmation__icon" aria-hidden="true">' +
			CHECK_ICON +
			'</div>' +
			'<h3 class="br-confirmation__heading">Booking Confirmed</h3>' +
			'<p class="br-confirmation__ref">#' +
			escapeHtml(String(b.id || '—')) +
			'</p>' +
			renderConfirmationDetails(b, paid, showPaid, offline) +
			'</div>'
		);
	}

	function paymentStatusLabelFromBooking(b) {
		if (b.is_refunded || b.payment_status === 'refunded' || b.status === 'refunded') {
			return 'Refunded';
		}
		if (b.payment_status_label) {
			return b.payment_status_label;
		}
		const status = b.payment_status || '';
		if (status === 'paid') {
			return 'Paid';
		}
		if (status === 'partial_paid') {
			return 'Partial paid';
		}
		if (status === 'refunded') {
			return 'Refunded';
		}
		return 'Unpaid';
	}

	function renderConfirmationDetails(b, paid, showPaid, offline) {
		const paymentStatus = paymentStatusLabelFromBooking(b);
		return (
			'<dl class="br-confirmation-grid">' +
			'<div><dt>Service</dt><dd>' +
			escapeHtml(state.service.name) +
			'</dd></div>' +
			'<div><dt>Staff</dt><dd>' +
			escapeHtml(state.staff.full_name) +
			'</dd></div>' +
			'<div><dt>Date</dt><dd>' +
			escapeHtml(formatDateLabel(state.date, displayTimeOptions())) +
			'</dd></div>' +
			'<div><dt>Time</dt><dd>' +
			escapeHtml(formatTimeLabel(state.time, state.date, displayTimeOptions())) +
			'</dd></div>' +
			'<div><dt>Payment status</dt><dd>' +
			escapeHtml(paymentStatus) +
			'</dd></div>' +
			'<div><dt>' +
			(showPaid ? 'Amount Paid' : 'Amount Due') +
			'</dt><dd>' +
			escapeHtml(
				formatMoney(
					showPaid
						? paid
						: Number(b.amount_total) || totalAfterCoupon(),
					currency()
				)
			) +
			'</dd></div></dl>' +
			(offline
				? '<p class="br-confirmation__ref">#' +
					escapeHtml(String(b.id || '—')) +
					'</p>'
				: '') +
			'<div class="br-confirmation__actions">' +
			'<button type="button" class="br-btn-outline" data-action="add-calendar">' +
			CALENDAR_ICON +
			' Add to Calendar</button>' +
			'<button type="button" class="br-text-link" data-action="book-another">Book Another</button>' +
			'</div>'
		);
	}

	function renderSummary(opts) {
		if (!state.service || !state.staff) {
			return '';
		}
		const sticky = opts && opts.sticky;
		const mobile = opts && opts.mobile;
		const inline = opts && opts.inline;
		const collapsed = state.summaryCollapsed && mobile;

		const discountLine =
			discountAmount() > 0
				? '<li class="br-summary-discount"><span>Discount</span><span>-' +
					escapeHtml(formatMoney(discountAmount(), currency())) +
					'</span></li>'
				: '';

		const body =
			'<ul class="br-summary-list">' +
			'<li><span>Service</span><span>' +
			escapeHtml(state.service.name) +
			'</span></li>' +
			'<li><span>Staff</span><span>' +
			escapeHtml(state.staff.full_name) +
			'</span></li>' +
			(state.date
				? '<li><span>Date & time</span><span>' +
					escapeHtml(formatShortDateLabel(state.date, displayTimeOptions())) +
					' · ' +
					escapeHtml(
						formatTimeLabel(state.time, state.date, displayTimeOptions())
					) +
					'</span></li>'
				: '') +
			'<li><span>Subtotal</span><span>' +
			escapeHtml(formatMoney(subtotal(), currency())) +
			'</span></li>' +
			discountLine +
			'<li class="br-summary-total"><span>Total</span><span>' +
			escapeHtml(formatMoney(totalAfterCoupon(), currency())) +
			'</span></li></ul>';

		if (mobile) {
			return (
				'<aside class="br-order-summary br-order-summary--mobile-toggle' +
				(collapsed ? ' is-collapsed' : '') +
				'">' +
				'<button type="button" class="br-order-summary__toggle" data-action="toggle-summary">' +
				'<span>' +
				escapeHtml(collapsed ? 'Show order summary ▼' : 'Hide order summary ▲') +
				'</span></button>' +
				'<div class="br-order-summary__body">' +
				body +
				'</div></aside>'
			);
		}

		const cls =
			'br-order-summary' +
			(sticky ? ' br-order-summary--sticky' : '') +
			(inline ? '' : '');

		return (
			'<aside class="' +
			cls +
			'">' +
			(inline ? '' : '<h3 class="br-order-summary__title">Order summary</h3>') +
			body +
			'</aside>'
		);
	}

	function showSidebar() {
		return state.step === 5;
	}

	function showMobileSummary() {
		return state.step === 5 || state.step === 6;
	}

	function isWideLayout() {
		return state.step === 5;
	}

	function toolbarHtml() {
		if (state.step >= 7) {
			return '';
		}
		const back =
			state.step > 1
				? '<button type="button" class="br-btn-secondary" data-action="back">← Back</button>'
				: '';
		return back ? '<div class="br-wizard-toolbar">' + back + '</div>' : '';
	}

	function render() {
		const wide = isWideLayout();
		const paymentStep = state.step === 6;
		const kitClass = root.dataset.elementorKitClass || '';
		root.className =
			'bookpress-booking-root' +
			(kitClass ? ' ' + kitClass : '') +
			(wide ? ' bookpress-booking-root--wide' : '') +
			(paymentStep ? ' bookpress-booking-root--payment' : '');

		const loadingBlock = state.loading
			? '<div class="br-wizard-loading" aria-live="polite"><span class="br-spinner"></span> Loading…</div>'
			: '';
		const errorBlock = state.error
			? '<div class="br-error-banner" role="alert">' +
				escapeHtml(state.error) +
				'</div>'
			: '';

		const header =
			'<h4 class="br-heading-lg br-wizard-header">' +
			escapeHtml(STEP_HEADERS[state.step] || '') +
			'</h4>';

		const sidebar = showSidebar()
			? renderSummary({ sticky: true, mobile: false })
			: '';
		const mobileSummary = showMobileSummary()
			? renderSummary({ mobile: true })
			: '';

		const layoutCls = showSidebar()
			? ' br-wizard-layout br-wizard-layout--with-sidebar'
			: ' br-wizard-layout';

		const wizardCls =
			'br-wizard' + (paymentStep ? ' br-wizard--has-fixed-cta' : '');

		root.innerHTML =
			'<div class="' +
			wizardCls +
			'">' +
			renderStepIndicator(state.step) +
			'<div class="br-wizard-panel">' +
			mobileSummary +
			'<div class="' +
			layoutCls.trim() +
			'">' +
			'<main class="br-wizard-main">' +
			header +
			errorBlock +
			loadingBlock +
			'<div class="br-wizard-body br-br-scrollbar" data-body>' +
			(state.loading && state.step === 1 && !state.services.length
				? ''
				: renderStepBody()) +
			'</div>' +
			toolbarHtml() +
			'</main>' +
			sidebar +
			'</div></div></div>';

		applyThemeVars(cfg.theme);


		bindEvents();
		if (state.step === 3) {
			startTzClock();
		}
	}

	function bindSlotClicks() {
		root.querySelectorAll('.br-slot[data-time]').forEach((btn) => {
			btn.addEventListener('click', () => {
				state.time = btn.getAttribute('data-time');
				render();
			});
		});
	}

	function bindEvents() {
		root.querySelector('[data-action="back"]')?.addEventListener('click', goBack);

		root.querySelectorAll('[data-go-step]').forEach((btn) => {
			btn.addEventListener('click', () => {
				goToStepFromTab(btn.getAttribute('data-go-step'));
			});
		});

		root.querySelectorAll('[data-service-id]').forEach((btn) => {
			btn.addEventListener('click', () => {
				const id = parseInt(btn.getAttribute('data-service-id'), 10);
				const s = state.services.find((x) => x.id === id);
				if (s) {
					selectService(s, false);
				}
			});
		});

		root.querySelectorAll('[data-staff-id]').forEach((btn) => {
			btn.addEventListener('click', () => {
				const id = parseInt(btn.getAttribute('data-staff-id'), 10);
				const s = state.staffList.find((x) => x.id === id);
				if (s) {
					selectStaff(s);
				}
			});
		});

		const calEl = root.querySelector('[data-calendar]');
		if (calEl && state.step === 3) {
			calendarWidget = createCalendar(calEl, {
				selectedDate: state.date,
				isDateDisabled,
				timezone: config.timezone || cfg.timezone || '',
				onMonthChange: (year, month) => {
					loadMonthAvailability(year, month);
				},
				onSelect: (dateStr) => {
					state.date = dateStr;
					state.time = '';
					loadSlots();
				},
			});
		}

		bindSlotClicks();

		root.querySelector('[data-action="datetime-continue"]')?.addEventListener('click', () => {
			if (state.date && state.time) {
				goToStep(4);
			}
		});

		const custForm = root.querySelector('[data-form="customer"]');
		if (custForm) {
			custForm.addEventListener('submit', (e) => {
				e.preventDefault();
				if (!validateCustomerForm(custForm)) {
					return;
				}
				const hp = custForm.querySelector('[name="website"]');
				if (hp && hp.value) {
					setError('Unable to submit. Please try again.');
					render();
					return;
				}
				state.customer = {
					first_name: custForm.querySelector('[name="first_name"]').value,
					last_name: custForm.querySelector('[name="last_name"]').value,
					email: custForm.querySelector('[name="email"]').value,
					phone: custForm.querySelector('[name="customer_phone"]').value || '',
					dob: custForm.querySelector('[name="customer_dob"]').value || '',
					parent_guardian_name:
						custForm.querySelector('[name="parent_guardian_name"]')?.value || '',
					parent_guardian_phone:
						custForm.querySelector('[name="parent_guardian_phone"]')?.value || '',
					street_address: custForm.querySelector('[name="street_address"]').value,
					city: custForm.querySelector('[name="city"]').value,
					state_province: custForm.querySelector('[name="state_province"]').value,
					postal_code: custForm.querySelector('[name="postal_code"]').value,
					referral_source:
						custForm.querySelector('[name="referral_source"]')?.value || '',
				};
				goToStep(5);
			});
		}

		const couponForm = root.querySelector('[data-form="coupon"]');
		if (couponForm) {
			couponForm.addEventListener('submit', (e) => {
				e.preventDefault();
				state.couponCode = couponForm.querySelector('[name="code"]').value;
				applyCoupon();
			});
		}

		root.querySelector('[data-action="skip-coupon"]')?.addEventListener('click', () => {
			state.coupon = null;
			goToStep(6);
		});
		root.querySelector('[data-action="coupon-continue"]')?.addEventListener('click', () =>
			goToStep(6)
		);
		root.querySelector('[data-action="remove-coupon"]')?.addEventListener('click', removeCoupon);

		root.querySelector('[data-action="toggle-summary"]')?.addEventListener('click', () => {
			state.summaryCollapsed = !state.summaryCollapsed;
			render();
		});

		root.querySelectorAll('.br-payment-card').forEach((card) => {
			card.addEventListener('click', () => {
				const radio = card.querySelector('input[type="radio"]');
				if (radio) {
					radio.checked = true;
					state.paymentMode = radio.value;
					cardMounted = false;
					unmountCard();
					render();
				}
			});
		});

		root.querySelectorAll('[name="payment_mode"]').forEach((radio) => {
			radio.addEventListener('change', () => {
				state.paymentMode = radio.value;
				cardMounted = false;
				unmountCard();
				render();
			});
		});

		if (state.step === 6 && !cardMounted) {
			const mode = state.paymentMode || defaultPaymentMode();
			if (mode === 'online' || mode === 'deposit') {
				const key = config.stripe_publishable_key || cfg.stripePublicKey;
				const cardEl = root.querySelector('#br-stripe-card');
				if (key && cardEl) {
					mountCardElement(key, cardEl).then(() => {
						cardMounted = true;
					});
				}
			}
		}

		root.querySelector('[data-action="pay"]')?.addEventListener('click', () => {
			const mode =
				root.querySelector('[name="payment_mode"]:checked')?.value ||
				defaultPaymentMode();
			state.paymentMode = mode;
			submitBooking();
		});

		root.querySelector('[data-action="add-calendar"]')?.addEventListener('click', () => {
			const b = state.booking;
			if (!b || !state.service) {
				return;
			}
			const ics = toIcsUtc(
				state.date,
				state.time,
				state.service.duration || 60,
				config.timezone || cfg.timezone
			);
			downloadIcs({
				id: b.id,
				title: state.service.name + ' — ' + config.business_name,
				description:
					'Booking #' +
					b.id +
					' with ' +
					state.staff.full_name,
				location: config.business_name || '',
				start: ics.start,
				end: ics.end,
			});
		});

		root.querySelector('[data-action="book-another"]')?.addEventListener('click', () => {
			window.location.reload();
		});
	}

	async function start() {
		root.innerHTML =
			'<div class="br-wizard"><p class="br-muted">Loading booking…</p></div>';
		await loadConfig();
		state.paymentMode = defaultPaymentMode();
		await loadServices();
	}

	start();
}
